/* Kexxoria sections (kx-*). One module for every page, loaded from snippets/scripts.liquid.
   - Scroll reveal, subtle parallax, hero video
   - Product: gallery, variant pills, quantity, AJAX add to cart (Horizon cart drawer), sticky bar
   Everything degrades to plain HTML: without this file, content is visible and the form posts to /cart/add. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

window.kxReady = true;
document.documentElement.classList.add('kx-js');

/* ---------- Scroll reveal ---------- */
function initReveal(root = document) {
  const items = root.querySelectorAll('[data-kx-reveal]:not(.is-in)');
  if (!items.length) return;
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => io.observe(el));
}

/* ---------- Parallax: a few pixels of drift, fine pointers only ---------- */
function initParallax() {
  const layers = [...document.querySelectorAll('[data-kx-parallax]')];
  if (!layers.length) return;
  const active = new Set();
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    active.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2); // -1..1
      const amount = Number(el.dataset.kxParallax) || 20;
      el.style.setProperty('--kx-py', `${(-progress * amount).toFixed(1)}px`);
    });
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => (entry.isIntersecting ? active.add(entry.target) : active.delete(entry.target)));
    onScroll();
  });

  const enable = () => {
    if (reduceMotion.matches || !finePointer.matches) {
      layers.forEach((el) => el.style.removeProperty('--kx-py'));
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      return;
    }
    layers.forEach((el) => io.observe(el));
    window.addEventListener('scroll', onScroll, { passive: true });
  };
  enable();
  reduceMotion.addEventListener?.('change', enable);
}

/* ---------- Hero video: respect reduced motion, pause off-screen ---------- */
function initVideos() {
  document.querySelectorAll('[data-kx-autoplay] video').forEach((video) => {
    if (reduceMotion.matches) {
      video.removeAttribute('autoplay');
      video.pause();
      video.controls = true;
      return;
    }
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) video.play().catch(() => {});
      else video.pause();
    }).observe(video);
  });
}

/* ---------- Product ---------- */
function money(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [whole, dec] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '${{amount}}')
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
}

class KxProduct {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form.kx-form');
    this.variants = JSON.parse(root.querySelector('[data-kx-variants]')?.textContent || '[]');
    this.moneyFormat = root.dataset.kxMoney;
    this.isProductPage = root.dataset.kxProductPage === 'true';
    this.initGallery();
    this.initVariants();
    this.initQuantity();
    this.initCart();
    this.initSticky();
    this.initDelivery();
    this.initCopy();
  }

  /* Copy the discount code; label confirms for a moment. */
  initCopy() {
    this.root.querySelectorAll('[data-kx-copy]').forEach((btn) => {
      const label = btn.querySelector('[data-kx-copy-label]');
      const idle = label?.textContent;
      btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(btn.dataset.kxCopy);
        } catch (error) {
          const range = document.createRange();
          range.selectNodeContents(btn.previousElementSibling);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        if (!label) return;
        label.textContent = btn.dataset.kxCopied;
        btn.dataset.state = 'copied';
        setTimeout(() => {
          label.textContent = idle;
          btn.dataset.state = '';
        }, 1600);
      });
    });
  }

  /* Gallery: stacked slides, crossfade, swipe on touch. */
  initGallery() {
    this.slides = [...this.root.querySelectorAll('[data-kx-slide]')];
    this.thumbs = [...this.root.querySelectorAll('[data-kx-thumb]')];
    this.count = this.root.querySelector('[data-kx-count]');
    this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.show(btn.dataset.kxThumb)));

    const stage = this.root.querySelector('[data-kx-stage]');
    if (!stage || this.slides.length < 2) return;
    let startX = 0;
    let startY = 0;
    stage.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
    });
    stage.addEventListener('pointerup', (e) => {
      const dx = e.clientX - startX;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(e.clientY - startY)) return;
      const i = this.slides.findIndex((s) => s.classList.contains('is-active'));
      const next = (i + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length;
      this.show(this.slides[next].dataset.kxSlide);
    });
  }

  show(mediaId) {
    if (!mediaId) return;
    let index = 0;
    this.slides.forEach((slide, i) => {
      const on = slide.dataset.kxSlide === String(mediaId);
      if (on) index = i;
      if (!on) slide.querySelector('video')?.pause();
      slide.classList.toggle('is-active', on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    this.thumbs.forEach((btn) => btn.setAttribute('aria-current', btn.dataset.kxThumb === String(mediaId) ? 'true' : 'false'));
    const thumb = this.thumbs[index];
    thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (this.count) this.count.textContent = `${index + 1} / ${this.slides.length}`;
  }

  /* Variant pills drive the hidden <select name="id">, which stays the source of truth. */
  initVariants() {
    this.select = this.root.querySelector('[data-kx-variant-select]');
    this.fieldsets = [...this.root.querySelectorAll('[data-kx-option]')];
    if (!this.select || !this.fieldsets.length) return;
    this.fieldsets.forEach((fs) => fs.addEventListener('change', () => this.onOptionChange()));
    this.updateAvailability();
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  }

  onOptionChange() {
    const chosen = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
    this.fieldsets.forEach((fs, i) => {
      const label = fs.querySelector('[data-kx-option-value]');
      if (label) label.textContent = chosen[i] || '';
    });
    this.updateAvailability();
    if (!variant) {
      this.setButtons(false, this.root.dataset.kxUnavailable);
      return;
    }
    this.select.value = String(variant.id);
    this.updatePrice(variant);
    this.setButtons(variant.available, variant.available ? this.root.dataset.kxAdd : this.root.dataset.kxSoldout);
    if (variant.featured_media?.id) this.show(variant.featured_media.id);
    if (this.isProductPage) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  updateAvailability() {
    const chosen = this.selectedOptions();
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const possible = this.variants.some(
          (v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || !chosen[j] || o === chosen[j])
        );
        input.closest('.kx-pill')?.toggleAttribute('data-unavailable', !possible);
      });
    });
  }

  updatePrice(variant) {
    this.root.querySelectorAll('[data-kx-price]').forEach((el) => (el.textContent = money(variant.price, this.moneyFormat)));
    const promo = this.root.querySelector('[data-kx-promo]');
    const promoPrice = promo?.querySelector('[data-kx-promo-price]');
    if (promoPrice) promoPrice.textContent = money(variant.price - Math.floor((variant.price * Number(promo.dataset.kxPromo)) / 100), this.moneyFormat);
    const was = this.root.querySelector('[data-kx-compare]');
    if (was) {
      const onSale = variant.compare_at_price > variant.price;
      was.hidden = !onSale;
      if (onSale) was.textContent = money(variant.compare_at_price, this.moneyFormat);
    }
  }

  setButtons(enabled, text) {
    this.root.querySelectorAll('[data-kx-add]').forEach((btn) => {
      btn.disabled = !enabled;
      const label = btn.querySelector('[data-kx-add-label]');
      if (label && text) label.textContent = text;
    });
  }

  initQuantity() {
    const qty = this.root.querySelector('input[name="quantity"]');
    this.root.querySelectorAll('[data-kx-qty]').forEach((btn) =>
      btn.addEventListener('click', () => {
        qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(btn.dataset.kxQty, 10));
      })
    );
  }

  /* AJAX add to cart, wired into Horizon's cart drawer through the standard cart event. */
  initCart() {
    if (!this.form) return;
    this.error = this.root.querySelector('[data-kx-error]');
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.add(event.submitter);
    });
  }

  async add(submitter) {
    const buttons = [...this.root.querySelectorAll('[data-kx-add]')];
    if (buttons.some((b) => b.dataset.state === 'adding')) return;
    buttons.forEach((b) => (b.dataset.state = 'adding'));
    if (this.error) this.error.hidden = true;

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    const quantity = Number(formData.get('quantity')) || 1;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    let didError = false;
    let sections;
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }).then((r) => r.json());
      if (response.status) {
        didError = true;
        this.showError(response.description || response.message);
        this.form.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description } }));
      } else {
        sections = response.sections;
      }
    } catch (error) {
      didError = true;
      this.showError(this.root.dataset.kxError);
    }

    try {
      const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'kx-product', sourceId: this.root.id, itemCount: quantity, productId: this.root.dataset.kxProductId, sections, didError },
      });
    } catch (error) {
      deferred.reject(error);
    }

    buttons.forEach((b) => (b.dataset.state = didError ? '' : 'added'));
    if (!didError) setTimeout(() => buttons.forEach((b) => (b.dataset.state = '')), 1600);
    submitter?.focus?.({ preventScroll: true });
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || this.root.dataset.kxError;
    this.error.hidden = false;
  }

  /* Delivery window as real dates, computed from today in the visitor's browser. */
  initDelivery() {
    const el = this.root.querySelector('[data-kx-delivery]');
    const out = el?.querySelector('span');
    const min = Number(el?.dataset.min);
    const max = Number(el?.dataset.max);
    if (!out || !el.dataset.template || !(max > 0)) return;
    const day = 864e5;
    const from = new Date(Date.now() + min * day);
    const to = new Date(Date.now() + max * day);
    const lang = document.documentElement.lang || 'es';
    const full = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' });
    const sameMonth = from.getMonth() === to.getMonth();
    const fromText = sameMonth ? String(from.getDate()) : full.format(from);
    out.textContent = el.dataset.template.replace('[desde]', fromText).replace('[hasta]', full.format(to));
  }

  initSticky() {
    const sticky = this.root.querySelector('[data-kx-sticky]');
    if (!sticky || !this.form || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.querySelectorAll('button').forEach((b) => (b.tabIndex = show ? 0 : -1));
    }).observe(this.form);
  }
}

function initProducts(root = document) {
  root.querySelectorAll('[data-kx-product]').forEach((el) => {
    if (el.kx) return;
    el.kx = new KxProduct(el);
  });
}

function init(root = document) {
  initReveal(root);
  initProducts(root);
}

init();
initParallax();
initVideos();

// Theme Editor: re-init sections when they are added or re-rendered.
document.addEventListener('shopify:section:load', (event) => init(event.target));
